const AUTOPLAY_DELAY = 5000;
const CAROUSEL_ROWS = [
	[".studio-clients", "Clients"],
	[".studio-industries", "Industries"],
	[".studio-testimonials", "Client reviews"],
	[".studio-service-process", "Our process"],
	[".services-single-page .cs-grid", "Case studies"],
];

export function mountMobileServiceCarousel(root) {
	prepareCarousels(root);
	const controllers = [...root.querySelectorAll("[data-mobile-card-carousel], [data-mobile-service-carousel]")]
		.filter((carousel) => !carousel.dataset.mobileCarouselMounted)
		.map((carousel) => {
			carousel.dataset.mobileCarouselMounted = "true";
			return new MobileServiceCarousel(carousel);
		});
	return () => controllers.forEach((controller) => controller.destroy());
}

function prepareCarousels(root) {
	CAROUSEL_ROWS.forEach(([selector, fallbackLabel]) => {
		root.querySelectorAll(selector).forEach((track) => {
			if (track.closest("[data-mobile-card-carousel], [data-mobile-service-carousel]") || track.children.length < 2) return;
			const carousel = document.createElement("div");
			const controls = document.createElement("div");
			const dots = document.createElement("div");
			const heading = track.closest("section")?.querySelector("h2")?.textContent.trim();
			carousel.className = "studio-mobile-carousel";
			carousel.dataset.mobileCardCarousel = "";
			carousel.setAttribute("aria-roledescription", "carousel");
			carousel.setAttribute("aria-label", heading || fallbackLabel);
			track.classList.add("studio-mobile-carousel__track");
			track.dataset.mobileCardTrack = "";
			controls.className = "studio-mobile-carousel__controls";
			dots.className = "studio-mobile-carousel__dots";
			dots.dataset.mobileCardDots = "";
			dots.setAttribute("aria-label", `Choose from ${heading || fallbackLabel}`);
			controls.append(dots);
			track.before(carousel);
			carousel.append(track, controls);
		});
	});
}

class MobileServiceCarousel {
	constructor(carousel) {
		this.carousel = carousel;
		this.carousel.classList.add("studio-mobile-carousel");
		this.track = carousel.querySelector("[data-mobile-card-track], [data-mobile-service-track]");
		this.track.classList.add("studio-mobile-carousel__track");
		this.slides = [...this.track.children];
		this.dots = carousel.querySelector("[data-mobile-card-dots], [data-mobile-service-dots]");
		this.dots.classList.add("studio-mobile-carousel__dots");
		this.dots.parentElement.classList.add("studio-mobile-carousel__controls");
		this.mobile = window.matchMedia("(max-width: 767px)");
		this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
		this.index = 0;
		this.timer = null;
		this.touchStart = null;
		this.abort = new AbortController();
		this.physicalIndex = 1;
		this.createClones();
		this.createDots();
		this.bindEvents();
		this.render();
		this.start();
	}

	createClones() {
		if (this.slides.length < 2) return;
		const first = this.slides[0].cloneNode(true);
		const last = this.slides.at(-1).cloneNode(true);
		[first, last].forEach((clone) => {
			clone.classList.add("is-carousel-clone");
			clone.setAttribute("aria-hidden", "true");
			clone.inert = true;
		});
		this.track.prepend(last);
		this.track.append(first);
	}

	createDots() {
		this.slides.forEach((slide, index) => {
			slide.setAttribute("role", "group");
			slide.setAttribute("aria-roledescription", "slide");
			slide.setAttribute("aria-label", `${index + 1} of ${this.slides.length}`);
			const dot = document.createElement("button");
			dot.type = "button";
			dot.setAttribute("aria-label", `Show service ${index + 1}`);
			dot.addEventListener("click", (event) => {
				this.goTo(index);
				if (event.detail) dot.blur();
			}, { signal: this.abort.signal });
			this.dots.append(dot);
		});
	}

	bindEvents() {
		const options = { signal: this.abort.signal };
		this.track.addEventListener("transitionend", () => this.finishLoop(), options);
		this.carousel.addEventListener("pointerenter", () => this.stop(), options);
		this.carousel.addEventListener("pointerleave", () => this.start(), options);
		this.carousel.addEventListener("focusin", () => this.stop(), options);
		this.carousel.addEventListener("focusout", () => this.start(), options);
		this.carousel.addEventListener("touchstart", (event) => {
			this.touchStart = event.touches[0]?.clientX ?? null;
			this.stop();
		}, { ...options, passive: true });
		this.carousel.addEventListener("touchend", (event) => {
			const end = event.changedTouches[0]?.clientX;
			if (this.touchStart !== null && end !== undefined && Math.abs(end - this.touchStart) > 45) {
				this.move(end < this.touchStart ? 1 : -1);
			}
			this.touchStart = null;
			this.start();
		}, { ...options, passive: true });
		document.addEventListener("visibilitychange", () => document.hidden ? this.stop() : this.start(), options);
		this.mobile.addEventListener("change", () => this.render(), options);
		this.reducedMotion.addEventListener("change", () => this.start(), options);
	}

	goTo(index) {
		this.index = (index + this.slides.length) % this.slides.length;
		this.physicalIndex = this.index + 1;
		this.render();
		this.start();
	}

	move(direction) {
		this.index = (this.index + direction + this.slides.length) % this.slides.length;
		this.physicalIndex += direction;
		this.render();
		this.start();
	}

	finishLoop() {
		if (this.physicalIndex > this.slides.length) this.physicalIndex = 1;
		else if (this.physicalIndex < 1) this.physicalIndex = this.slides.length;
		else return;
		this.track.classList.add("is-jumping");
		this.render();
		this.track.getBoundingClientRect();
		this.track.classList.remove("is-jumping");
	}

	render() {
		const active = this.mobile.matches;
		this.track.style.transform = active ? `translate3d(-${this.physicalIndex * 100}%, 0, 0)` : "";
		this.slides.forEach((slide, index) => {
			const hidden = active && index !== this.index;
			slide.setAttribute("aria-hidden", String(hidden));
			slide.inert = hidden;
		});
		[...this.dots.children].forEach((dot, index) => {
			dot.classList.toggle("is-active", index === this.index);
			dot.setAttribute("aria-current", index === this.index ? "true" : "false");
		});
	}

	start() {
		this.stop();
		if (!this.mobile.matches || this.reducedMotion.matches || document.hidden || this.carousel.matches(":focus-within")) return;
		this.timer = window.setInterval(() => this.move(1), AUTOPLAY_DELAY);
	}

	stop() {
		window.clearInterval(this.timer);
		this.timer = null;
	}

	destroy() {
		this.stop();
		this.abort.abort();
		this.track.querySelectorAll(".is-carousel-clone").forEach((clone) => clone.remove());
	}
}
